/**
 * Secure Deal Box - Firebase Service Hook
 * Handles file upload, download, sharing, versioning, and event logging
 */

import { useState, useCallback } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  getDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '@/lib/firebase';
import type { 
  DealFile, 
  DealShared, 
  FileEvent, 
  UploadFileRequest, 
  ShareFileRequest,
  DealBoxFilters,
  VisibilityRole,
  DocumentType,
  DocumentCategory,
  FileEventType
} from '@/types/deal-box.types';
import { PERMISSION_RULES } from '@/constants/deal-box.constants';

export function useDealBox(dealId?: string) {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Get user's role from claims (simplified - in production would use Firebase claims)
  const getUserRole = useCallback(async (): Promise<VisibilityRole> => {
    if (!user) return 'buyer';
    const token = await user.getIdTokenResult();
    if (token.claims.admin) return 'admin';
    if (token.claims.staff) return 'staff';
    if (token.claims.broker) return 'broker';
    if (token.claims.seller) return 'seller';
    if (token.claims.lender) return 'lender';
    if (token.claims.consultant) return 'consultant';
    return 'buyer';
  }, [user]);

  // Get permission rules for current user
  const getPermissions = useCallback(async () => {
    const role = await getUserRole();
    return PERMISSION_RULES.find(r => r.role === role) || PERMISSION_RULES[0];
  }, [getUserRole]);

  // Log file event
  const logFileEvent = useCallback(async (
    fileId: string,
    eventType: FileEventType,
    metadata?: Record<string, unknown>
  ) => {
    if (!user || !dealId) return;
    
    const role = await getUserRole();
    const event: Omit<FileEvent, 'id'> = {
      fileId,
      dealId,
      eventType,
      actorId: user.uid,
      actorRole: role,
      createdAt: new Date(),
      metadata,
    };

    try {
      await addDoc(collection(db, 'fileEvents'), {
        ...event,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to log file event:', err);
    }
  }, [user, dealId, getUserRole]);

  // Upload file
  const uploadFile = useCallback(async (request: UploadFileRequest): Promise<DealFile | null> => {
    if (!user) {
      setError('User not authenticated');
      return null;
    }

    setLoading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const role = await getUserRole();
      const permissions = await getPermissions();
      
      if (!permissions.canUpload) {
        setError('You do not have permission to upload files');
        return null;
      }

      // Generate storage path
      const timestamp = Date.now();
      const sanitizedFilename = request.file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `dealFiles/${request.dealId}/${user.uid}/${timestamp}_${sanitizedFilename}`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, request.file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            setError('Failed to upload file');
            setLoading(false);
            reject(error);
          },
          async () => {
            try {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              
              // Determine version
              let version = 1;
              let previousVersionId: string | undefined;
              
              if (request.replaceFileId) {
                const prevDoc = await getDoc(doc(db, 'dealFiles', request.replaceFileId));
                if (prevDoc.exists()) {
                  const prevData = prevDoc.data();
                  version = (prevData.version || 1) + 1;
                  previousVersionId = request.replaceFileId;
                  
                  // Archive previous version
                  await updateDoc(doc(db, 'dealFiles', request.replaceFileId), {
                    isArchived: true,
                  });
                }
              }

              // Create Firestore document
              const fileData: Omit<DealFile, 'id'> = {
                dealId: request.dealId,
                uploadedByUserId: user.uid,
                uploadedByRole: role,
                uploadedAt: new Date(),
                fileUrl: downloadUrl,
                filename: sanitizedFilename,
                originalFilename: request.file.name,
                fileType: request.file.type,
                fileSize: request.file.size,
                documentType: request.documentType,
                category: request.category,
                title: request.title,
                description: request.description,
                version,
                previousVersionId,
                visibilityRoles: request.visibilityRoles,
                sharedWith: [],
                storagePath,
                isArchived: false,
              };

              const docRef = await addDoc(collection(db, 'dealFiles'), {
                ...fileData,
                uploadedAt: serverTimestamp(),
              });

              // Log event
              await logFileEvent(docRef.id, request.replaceFileId ? 'replace' : 'upload', {
                previousVersion: previousVersionId ? version - 1 : undefined,
                newVersion: version,
              });

              setLoading(false);
              setUploadProgress(100);
              resolve({ ...fileData, id: docRef.id });
            } catch (err) {
              console.error('Failed to save file metadata:', err);
              setError('Failed to save file metadata');
              setLoading(false);
              reject(err);
            }
          }
        );
      });
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Upload failed');
      setLoading(false);
      return null;
    }
  }, [user, getUserRole, getPermissions, logFileEvent]);

  // Fetch files for deal
  const fetchFiles = useCallback(async (filters?: DealBoxFilters): Promise<DealFile[]> => {
    if (!dealId) return [];

    setLoading(true);
    setError(null);

    try {
      const role = await getUserRole();
      let q = query(
        collection(db, 'dealFiles'),
        where('dealId', '==', dealId),
        where('isArchived', '==', false),
        orderBy('uploadedAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const files: DealFile[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        // Check visibility
        if (data.visibilityRoles.includes(role) || role === 'admin' || role === 'staff') {
          const file: DealFile = {
            id: doc.id,
            dealId: data.dealId,
            uploadedByUserId: data.uploadedByUserId,
            uploadedByRole: data.uploadedByRole,
            uploadedAt: data.uploadedAt?.toDate() || new Date(),
            fileUrl: data.fileUrl,
            filename: data.filename,
            originalFilename: data.originalFilename,
            fileType: data.fileType,
            fileSize: data.fileSize,
            documentType: data.documentType,
            category: data.category,
            title: data.title,
            description: data.description,
            version: data.version,
            previousVersionId: data.previousVersionId,
            visibilityRoles: data.visibilityRoles,
            sharedWith: data.sharedWith || [],
            storagePath: data.storagePath,
            isArchived: data.isArchived,
            metadata: data.metadata,
          };

          // Apply client-side filters
          let include = true;
          if (filters?.documentType && file.documentType !== filters.documentType) include = false;
          if (filters?.category && file.category !== filters.category) include = false;
          if (filters?.uploadedBy && file.uploadedByUserId !== filters.uploadedBy) include = false;
          if (filters?.searchQuery) {
            const search = filters.searchQuery.toLowerCase();
            if (!file.title.toLowerCase().includes(search) && 
                !file.filename.toLowerCase().includes(search)) {
              include = false;
            }
          }
          if (filters?.dateFrom && file.uploadedAt < filters.dateFrom) include = false;
          if (filters?.dateTo && file.uploadedAt > filters.dateTo) include = false;

          if (include) files.push(file);
        }
      });

      setLoading(false);
      return files;
    } catch (err) {
      console.error('Failed to fetch files:', err);
      setError('Failed to fetch files');
      setLoading(false);
      return [];
    }
  }, [dealId, getUserRole]);

  // Share file
  const shareFile = useCallback(async (request: ShareFileRequest): Promise<boolean> => {
    if (!user) {
      setError('User not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const permissions = await getPermissions();
      if (!permissions.canShare) {
        setError('You do not have permission to share files');
        return false;
      }

      // Create share record
      const shareData: Omit<DealShared, 'id'> = {
        dealId: request.dealId,
        fileId: request.fileId,
        sharedToRole: request.shareToRole,
        sharedToUserId: request.shareToUserId,
        createdAt: new Date(),
        createdBy: user.uid,
        isAcknowledged: false,
      };

      await addDoc(collection(db, 'dealShared'), {
        ...shareData,
        createdAt: serverTimestamp(),
      });

      // Update file's sharedWith if sharing to specific user
      if (request.shareToUserId) {
        const fileRef = doc(db, 'dealFiles', request.fileId);
        const fileDoc = await getDoc(fileRef);
        if (fileDoc.exists()) {
          const currentShared = fileDoc.data().sharedWith || [];
          if (!currentShared.includes(request.shareToUserId)) {
            await updateDoc(fileRef, {
              sharedWith: [...currentShared, request.shareToUserId],
            });
          }
        }
      }

      // Update visibility roles if sharing to role
      if (request.shareToRole) {
        const fileRef = doc(db, 'dealFiles', request.fileId);
        const fileDoc = await getDoc(fileRef);
        if (fileDoc.exists()) {
          const currentRoles = fileDoc.data().visibilityRoles || [];
          if (!currentRoles.includes(request.shareToRole)) {
            await updateDoc(fileRef, {
              visibilityRoles: [...currentRoles, request.shareToRole],
            });
          }
        }
      }

      // Log event
      await logFileEvent(request.fileId, 'share', {
        sharedTo: request.shareToUserId || request.shareToRole,
      });

      setLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to share file:', err);
      setError('Failed to share file');
      setLoading(false);
      return false;
    }
  }, [user, getPermissions, logFileEvent]);

  // Delete file
  const deleteFile = useCallback(async (fileId: string): Promise<boolean> => {
    if (!user) {
      setError('User not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const fileRef = doc(db, 'dealFiles', fileId);
      const fileDoc = await getDoc(fileRef);
      
      if (!fileDoc.exists()) {
        setError('File not found');
        return false;
      }

      const fileData = fileDoc.data();
      const role = await getUserRole();

      // Check permission - only uploader, admin, or staff can delete
      if (fileData.uploadedByUserId !== user.uid && role !== 'admin' && role !== 'staff') {
        setError('You do not have permission to delete this file');
        return false;
      }

      // Delete from storage
      try {
        const storageRef = ref(storage, fileData.storagePath);
        await deleteObject(storageRef);
      } catch (storageErr) {
        console.warn('Could not delete from storage:', storageErr);
      }

      // Delete Firestore document
      await deleteDoc(fileRef);

      // Log event
      await logFileEvent(fileId, 'delete');

      setLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to delete file:', err);
      setError('Failed to delete file');
      setLoading(false);
      return false;
    }
  }, [user, getUserRole, logFileEvent]);

  // Get file version history
  const getVersionHistory = useCallback(async (fileId: string): Promise<DealFile[]> => {
    if (!fileId) return [];

    try {
      const versions: DealFile[] = [];
      let currentId: string | undefined = fileId;

      while (currentId) {
        const docRef = doc(db, 'dealFiles', currentId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) break;
        
        const data = docSnap.data();
        versions.push({
          id: docSnap.id,
          dealId: data.dealId,
          uploadedByUserId: data.uploadedByUserId,
          uploadedByRole: data.uploadedByRole,
          uploadedAt: data.uploadedAt?.toDate() || new Date(),
          fileUrl: data.fileUrl,
          filename: data.filename,
          originalFilename: data.originalFilename,
          fileType: data.fileType,
          fileSize: data.fileSize,
          documentType: data.documentType,
          category: data.category,
          title: data.title,
          description: data.description,
          version: data.version,
          previousVersionId: data.previousVersionId,
          visibilityRoles: data.visibilityRoles,
          sharedWith: data.sharedWith || [],
          storagePath: data.storagePath,
          isArchived: data.isArchived,
        });

        currentId = data.previousVersionId;
      }

      return versions;
    } catch (err) {
      console.error('Failed to get version history:', err);
      return [];
    }
  }, []);

  // Get file events/audit log
  const getFileEvents = useCallback(async (fileId: string): Promise<FileEvent[]> => {
    try {
      const q = query(
        collection(db, 'fileEvents'),
        where('fileId', '==', fileId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const events: FileEvent[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        events.push({
          id: doc.id,
          fileId: data.fileId,
          dealId: data.dealId,
          eventType: data.eventType,
          actorId: data.actorId,
          actorRole: data.actorRole,
          createdAt: data.createdAt?.toDate() || new Date(),
          metadata: data.metadata,
        });
      });

      return events;
    } catch (err) {
      console.error('Failed to get file events:', err);
      return [];
    }
  }, []);

  // Download file (logs event)
  const downloadFile = useCallback(async (file: DealFile) => {
    await logFileEvent(file.id, 'download');
    window.open(file.fileUrl, '_blank');
  }, [logFileEvent]);

  return {
    loading,
    uploadProgress,
    error,
    uploadFile,
    fetchFiles,
    shareFile,
    deleteFile,
    downloadFile,
    getVersionHistory,
    getFileEvents,
    getPermissions,
    getUserRole,
  };
}
